import yup from 'shared/validation/yupExtensions';
import { FilteredDropDownWithTypeModel, GetPermissionsResponse, GroupListModel, ListModel, ListModelWithBoolValue, ListModelWithDetail, ListModelWithIntValue, TranslationList, TranslationUDF } from 'shared/interfaces/core-interfaces';
import { TranslationUDFScheme } from "../../TextblockTemplates/state/textblock-template-interfaces";
import { BatchUpdateActions, nullDate } from "shared/utils/common";
import { isValidIban, normalizeIban } from "shared/utils/iban";
import { isBicProvided, isValidBic } from "shared/utils/bic";
import { AdditionalPdfDataModel, AdditionalPdfDataModelInit, RecipientPdfData, TenantPdfData } from './finance-pdf-interfaces';
import { FinanceDocumentType, ProcedureType, XmlPaymentType } from "constants/enums";
import dayjs from 'shared/dayjs';
import { Guid } from 'guid-typescript';
import { RequestForOverdueCalculation } from '../../../TenantSettings/Requests/state/request-interfaces';
import { ArticleModel, UnitModel } from '../../../CorporationSettings/referenceDataFeatures/state/reference-data-interface';

export interface FinanceDocumentResponse {
    requestStatusId?: number | null;
    basicInfo: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo | DunningBasicInfo;
    procedure: ProcedureInfo;
    positions: Position[] | ReminderPosition[];
    textBlocks: TextBlock[];
    files: Attachment[],
    documents: DocumentPdfModel[]
    relatedFinanceDocumentNumbers: FinanceDocumentNumbers,
    pdfSettings: AdditionalPdfDataModel
}

export interface CreateFinanceDocumentResponse {
    id: Guid,
    documentNumber: string | null,
    message: string
}

export interface FinanceDocumentApprovalResponse {
    message: string;
    requestStatusId?: number | null;
    basicInfo: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo | DunningBasicInfo;
    procedure: ProcedureInfo;
    positions: Position[] | ReminderPosition[];
    textBlocks: TextBlock[];
    files: Attachment[],
    documents: DocumentPdfModel[]
    relatedFinanceDocumentNumbers: FinanceDocumentNumbers,
    projectSelectListItem: ListModel<string>[] | []
}

export interface CreateFinanceDocumentRequest {
    basicInfo: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo | DunningBasicInfo;
    procedure: ProcedureInfo;
    textBlocks: TextBlock[];
    positions: Position[];
    files: Attachment[];
    actionType: string;
}

export interface CreateReminderDunningRequest {
    basicInfo: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo | DunningBasicInfo;
    procedure: ProcedureInfo;
    textBlocks: TextBlock[];
    reminderPositions: ReminderPosition[];
    files: Attachment[];
    actionType: string;
}

export interface FinanceDocumentDropdownLists {
    partners: ListModel<string>[] | [],
    projects: ListModel<string>[] | [],
    statuses: ListModel<number>[] | [],
    contractTypeList: ListModel<number>[] | [],
    procedureTypes: ListModel<number>[] | [],
    paymentIntervals: ListModel<string>[] | [],
    /*    tenantsAndPartners: GroupListModel<string>[] | [], */
    articles: TranslationList[] | [],
    positionUnits: UnitModel[] | [],
    currencies: ListModel<string>[] | [],
    documentTypes: ListModel<number>[] | [],
    textBlockPositions: ListModel<number>[] | [],
    sources: TranslationList[] | [],
    accountingNumbers: TranslationList[] | [],
    categories: TranslationList[] | [],
    paymentMethods: TranslationList[] | [],
    extensions: TranslationList[] | [],
    financeDocumentAccessableTenants: ListModelWithBoolValue<string>[] | [],
    commercialDocumentsAccessableTenants: ListModelWithBoolValue<string>[] | [],
    documentNumber: string,
}

export interface FinanceDocumentSliceModel {
    statusCode: string,
    isLoading: boolean,
    isModalLoading: boolean,
    message: string,
    showNotification: boolean,
    isUploadSpaceOpen: boolean,
    dropdownLists: FinanceDocumentDropdownLists | null,
    filteredPartnersAndTenantsList: FilteredDropDownWithTypeModel,
    tenantDropdownLists: PartnerTenantDropdownLists,
    partnerDropdownLists: PartnerTenantDropdownLists,
    tenantPdfData: TenantPdfData | null,
    recipientPdfData: RecipientPdfData | null,
    financeDocumentId: string,
    prevFinanceDocument: { id: string | null, propName: string },
    financeDocumentTypeId: string,
    financeDocumentRequestStatusId?: number | null,
    relatedFinanceDocumentNumbers: FinanceDocumentNumbers,
    financeDocumentCreateModelValid: boolean,
    projectData: ProjectData,
    basicInfoTabData: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo,
    procedureInfoTabData: ProcedureTabData;
    textblockTabData: TextBlockTabData,
    attachmentsTabData: AttachmentsTabData,
    positionsTabData: PositionsTabData,
    paymentsTabData: PaymentDataResponse,
    pdfTabData: PdfTabData,
    documentLists: DocumentLists,
    permissions: GetPermissionsResponse,
    exportFileDataTab: ExportFileDataResponseTab,
    financeDocumentDatabaseState: {
        basicInfoTabData: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo,
        procedureInfo: ProcedureInfo;
    },
    sourceInfo: SourceInfo,
    categoryInfo: CategoryInfo,
    paymentMethodInfo: PaymentMethodInfo,
    /* reminderPositionPayload: ReminderPositionPayload, */
    invoiceAndPaymentDetails: InvoceAndPaymentSumAmountResponse
}

export interface FinanceDocumentNumbers {
    requestNumber?: string | null,
    offerNumber?: string | null,
    orderNumber?: string | null,
    orderConfirmationNumber?: string | null,
    invoiceNumber?: string | null,
    creditNoteNumber?: string | null,
    reminderNumber?: string | null,
    dunningNumber?: string | null,
}

//#region TableList

export interface CostCenterData {
    costCenterId: string,
    costCenterNumber: string,
    costCenterName: string,
    startDate: Date,
    endDate: Date,
    tenantTechnicalProjectLeadAbv: string,
    tenantFinanceProjectLeadAbv: string,
    salesPersonAbv: string,
    intermediaryPerson: string,
}

export interface CostCenterDetails {
    id: string,
    isOutgoing: boolean,
    documentTypeId: number,
    documentType: string,
    documentStatus: string,
    documentNumber: string,
    documentAbv: string,
    documentDate: Date,
    articles: ArticleModel[] | [],
    subject: string,
    currency: string,
    clientCompanyName: string,
    clientNumber: string,
    clientResponsiblePerson: string,
    clientCostCenterNumber: string,
    tenantAbv: string,
    tenantResponsiblePersonAbv: string,
    comment: string,
    totalTax: number,
    totalNet: number,
    totalBrutto: number,
    paymentDays: number,
    paymentDeadLine: Date,
    daysOverdue: number,
    amountsPayed: TotalPaymentByCurrency[],
    createdBy: string,
    editedBy: string,
    createdAt: Date,
    editedAt: Date,
    files: any[],
    documents: DocumentPdfModel[],
}

export interface CostCenterDetailsResponse {
    costCenterId: string,
    documentList: CostCenterDetails[]
}

export interface DocumentLists {
    activeRequests: RequestListItem[],
    inactiveRequests: RequestListItem[],
    activeOffers: OfferListItem[],
    inactiveOffers: OfferListItem[],
    activeOrders: OrderListItem[],
    inactiveOrders: OrderListItem[],
    activeOrderConfirmations: OrderConfirmationListItem[],
    inactiveOrderConfirmations: OrderConfirmationListItem[],
    activeInvoices: InvoiceListItem[],
    inactiveInvoices: InvoiceListItem[],
    activeCreditNotes: CreditNoteListItem[],
    inactiveCreditNotes: CreditNoteListItem[],
    activeReminders: ReminderListItem[],
    inactiveReminders: ReminderListItem[],
    contractList: ContractListItem[],
    overviewsByCostCenter: CostCenterData[],
    costCenterDetails: CostCenterDetailsResponse[],
}

/** The document lists that a row can be deactivated, reactivated or deleted out of. */
export type DocumentListName = Exclude<keyof DocumentLists, "contractList" | "overviewsByCostCenter" | "costCenterDetails">;

/** A row put back into its list after the request that took it out failed. */
export interface ListRowToRestore {
    list: DocumentListName,
    index: number,
    item: DocumentListItem,
}

export interface DocumentListItem {
    id: string,
    documentTypeId: number;
    documentStatus: string,
    requestStatus: string,
    comment: string,
    documentNumber: string,
    documentAbv: string,
    documentDate: Date,
    subject: string,
    costCenterNumber: string,
    costCenterName: string,
    costCentarDecription: string, // mozda da se izvadi iz DocumentListItem
    startDate: Date,
    endDate: Date,
    contractType: string,
    clientCompanyName: string,
    clientNumber: string,
    clientResponsiblePerson: string,
    clientLocation: string,
    clientTechnicalProjectLead: string,
    clientFinanceProjectLead: string,
    clientAccountingContact: string,
    clientSalesPerson: string,
    clientIntermediaryPerson: string,
    tenantAbv: string,
    tenantName: string,
    tenantLocation: string,
    tenantResponsiblePersonAbv: string;
    tenantResponsiblePersonName: string,
    tenantTechnicalProjectLeadAbv: string,
    tenantTechnicalProjectLeadName: string,
    tenantFinanceProjectLeadAbv: string,
    tenantFinanceProjectLeadName: string,
    tenantAccountingContactAbv: string,
    tenantAccountingContactName: string,
    salesPersonAbv: string,
    salesPersonName: string,
    intermediaryPersonAbv: string,
    intermediaryPersonName: string,
    daysOverdue?: number | null,
    totalTax: number,
    totalNet: number,
    totalBrutto: number,
    currency: string,
    createdByAbv: string,
    createdByName: string,
    createdAt: Date,
    editedByAbv: string,
    editedByName: string,
    editedAt: Date,
    documentDirection: string,
    files: DocumentPdfModel[],
    documents: DocumentPdfModel[],
    lastRequest: RequestForOverdueCalculation | null,
    procedureName: string,
    totalPersonnelHours: number;
}

export interface RequestListItem extends DocumentListItem {
    source: string,
    articles: ArticleModel[],
}
export interface OfferListItem extends DocumentListItem {
    requestNumber: string,
    requestDate: Date,
    articles: ArticleModel[],
}
export interface OrderListItem extends DocumentListItem {
    requestNumber: string,
    requestDate: Date,
    offerNumber: string,
    offerDate: Date,
    articles: ArticleModel[],
}
export interface OrderConfirmationListItem extends DocumentListItem {
    requestNumber: string,
    requestDate: Date,
    offerNumber: string,
    offerDate: Date,
    orderNumber: string,
    orderDate: Date,
    articles: ArticleModel[],
}

export interface DocumentPayments {
    paymentDate: Date,
    amount: number,
    currency: string
}
export interface InvoiceListItem extends DocumentListItem {
    customInvoiceNumber: string,
    periodFrom: Date,
    periodTo: Date,
    delivateDateFrom: Date,
    delivateDateTo: Date,
    securityDeposit: boolean | null,
    depositPercent: number | null,
    clientCostCenterNumber: string,
    partnerSalesPerson: string,
    partnerAccountingContact: string,
    paymentDeadLine: Date,
    /*  amountsPayed: TotalPaymentByCurrency[], */
    hasPaymentData: boolean,
    paymentMethod: string | null,
    requestNumber: string,
    offerNumber: string,
    orderNumber: string,
    orderConfirmationNumber: string,
    payments: PaymentDTO[],
    creditNotes?: CreditNoteDTO[] | [],
    articles: ArticleModel[],
    offerDocument?: DocumentPdfModel | null,
    orderDocument?: DocumentPdfModel | null,
    orderConfirmationDocument?: DocumentPdfModel | null,
    paidByCash: boolean;
    invoicePaymentMethod?: TranslationList;
    totalPersonnelHours: number;
}

export interface CreditNoteListItem extends InvoiceListItem {
    invoiceNumber: string,
}

export interface PaymentDTO {
    paidOn: Date,
    amount: number,
    currency: string,
    bankCommission: number | null
}

export interface CreditNoteDTO {
    id: string,
    creditNoteNumber: string,
    net: number,
    gross: number,
    tax: number,
    statusId: number
}

export interface ReminderListItem extends DocumentListItem {
    periodFrom: Date,
    periodTo: Date,
    delivateDateFrom: Date,
    delivateDateTo: Date,
    securityDeposit: boolean | null,
    depositPercent: number | null,
    clientCostCenterNumber: string,
    partnerSalesPerson: string,
    partnerAccountingContact: string,
    paymentDeadLine: Date,
    /*  amountsPayed: TotalPaymentByCurrency[], */
    hasPaymentData: boolean,
    requestNumber: string,
    offerNumber: string,
    orderNumber: string,
    orderConfirmationNumber: string,
    payments: PaymentDTO[],
    invoiceAmount: number,
}

export interface DunningListItem extends DocumentListItem {
    periodFrom: Date,
    periodTo: Date,
    delivateDateFrom: Date,
    delivateDateTo: Date,
    securityDeposit: boolean | null,
    depositPercent: number | null,
    clientCostCenterNumber: string,
    partnerSalesPerson: string,
    partnerAccountingContact: string,
    paymentDeadLine: Date,
    /*  amountsPayed: TotalPaymentByCurrency[], */
    hasPaymentData: boolean,
    requestNumber: string,
    offerNumber: string,
    orderNumber: string,
    orderConfirmationNumber: string,
    payments: PaymentDTO[],
    invoiceAmount: number,
}

export interface GetFinanceRequestsResponse extends GetPermissionsResponse {
    requests: RequestListItem[]
}

export interface GetOffersResponse extends GetPermissionsResponse {
    offers: OfferListItem[]
}

export interface GetOrdersResponse extends GetPermissionsResponse {
    orders: OrderListItem[]
}

export interface GetOrderConfirmationsResponse extends GetPermissionsResponse {
    orderConfirmations: OrderConfirmationListItem[]
}

export interface GetInvoicesResponse extends GetPermissionsResponse {
    invoices: InvoiceListItem[]
}

export interface GetCreditNotesResponse extends GetPermissionsResponse {
    creditNotes: CreditNoteListItem[]
}

export interface GetRemindersResponse extends GetPermissionsResponse {
    reminders: ReminderListItem[]
}

export interface GetDunningsResponse extends GetPermissionsResponse {
    dunningList: DunningListItem[]
}

export interface TotalPaymentByCurrency {
    sum: number;
    currency: string;
}

export interface DocumentsAndFilesRequest {
    documentId: string;
    documentTypeId: number;
}

export interface OrderConfirmationListResponse {
    orderConfirmations: OrderConfirmationListItem[]
}

export interface InvoiceListResponse {
    invoices: InvoiceListItem[]
}

export interface ReminderListResponse {
    reminders: ReminderListItem[]
}

export interface DocumentsAndFilesResponse {
    documentId: string;
    documentTypeId: number;
    files: DocumentPdfModel[];
    documents: DocumentPdfModel[];
    offerDocument?: DocumentPdfModel | null;
    orderDocument?: DocumentPdfModel | null;
    orderConfirmationDocument?: DocumentPdfModel | null;
}

//Contract list
export interface ContractListItem {
    id: string,
    status: string;
    isOutgoing: string;
    documentDate: Date;
    category: string;
    documentType: string;
    documentTypeId: number;
    subject: string;
    tenant: string;
    clientCompany: string;
    department: string;
    costCenter: string;
    procedure: string;
    contractStartDate: Date;
    contractEndDate: Date;

    oneTimeCostNet: number;
    oneTimeCostGross: number;
    ongoingCostNet: number;
    ongoingCostGross: number;
    currency: string;

    extensions: string;
    noticePeriod: number | null;
    paymentInterval: string;
    paymentMethod: string;
    note: string;

    files: DocumentPdfModel[];
    documents: DocumentPdfModel[];
}

export interface GetContractListRequest {
    projects: string[] | null;
    procedures: string[] | null;
    clients: string[] | null;
}

export interface GetFileListForContractItemRequest {
    id: string;
    documentType: string;
}

export interface GetFileListForContractItemResponse {
    id: string;
    documentType: string;
    files: DocumentPdfModel[];
    documents: DocumentPdfModel[];
}

//#endregion

//#region BasicInfo

export interface PartnerTenantDropdownLists {
    locations: ListModel<string>[] | [],
    employees: ListModel<string>[] | [],
    bankDatas: ListModel<string>[] | [],
    projects?: ListModel<string>[] | [],
    abbreviation?: string
    tenantInvoiceEmail?: string
}

export interface PartnerDropdownListsResponse {
    locations: ListModel<string>[] | [],
    contacts: ListModel<string>[] | [],
    bankDatas: ListModel<string>[] | [],
}

export interface TenantLocationBankDataResponse {
    bankDatas: ListModelWithBoolValue<string>[] | [],
    city: string;
}

export interface UpdateDocumentStatus {
    id: string,
    statusId: string
}

export interface CheckDocumentNumberRequest {
    documentTypeId: number,
    documentNumber: string
}

export interface SetPropValue {
    propName: string,
    value: any
}

export interface UpdateBasicInfoResponse {
    message: string,
    documentNumber: string | null,
    documentStatusId: number
}

export interface BasicInfo {
    id: string;
    isOutgoing: boolean;
    typeId?: string | null;
    statusId: number;
    documentNumber: string;

    documentDate?: Date | null;

    currency?: string;
    subject: string;
    note: string;
    comment?: string;

    reverseChargeTax?: boolean | null;
    vat?: number | null;
}

const BasicInfoRequestModelScheme = yup.object().shape({
    id: yup.string().defined(),
    isOutgoing: yup.boolean().required("Outgoing status is required."),
    typeId: yup.string().nullable().notRequired(),
    statusId: yup.number().min(1, "Status is required.").required("Status is required."),
    documentNumber: yup.string().when('isOutgoing', {
        is: (isOutgoing: boolean) => isOutgoing === false,
        then: (schema) => schema.required("Document number is required."),
        otherwise: (schema) => schema.notRequired()
    }).defined(),

    documentDate: yup.date().typeError("Please type valid date.")/* .max(dayjs().endOf('day').toDate(), "The entered date cannot be later than today.") */
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === false,
            then: (schema) => schema.required("Document date is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable().notRequired(),

    subject: yup.string().required("Subject is required."),
    note: yup.string().defined(),
    comment: yup.string().when('statusId', {
        is: (value: number) => value === 5 || value === 6,
        then: (schema) => schema.required("Status comment is required."),
        otherwise: (schema) => schema.notRequired()
    }),

    reverseChargeTax: yup.boolean().nullable(),
    vat: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "VAT needs to be between 0 and 100.").max(100, "VAT needs to be between 0 and 100.").nullable(),
});

export const initialBasicInfo: RequestBasicInfo | OfferBasicInfo | OrderBasicInfo | OrderConfirmationBasicInfo | InvoiceBasicInfo | ReminderBasicInfo = {
    id: "",
    isOutgoing: false,
    typeId: null,
    statusId: 0,
    documentNumber: "",
    bindingDeadline: null,
    documentDate: null,
    currency: "",
    subject: "",
    note: "",
    comment: "",
    paymentDays: null,
    discountDays: null,
    discount: null,
    deliveryPeriodFrom: null,
    deliveryPeriodTo: null,
    periodFrom: null,
    periodTo: null,
    documentTypeId: 0,
    requestId: null,
    offerId: null,
    orderId: null,
    orderConfirmationId: null,
    invoiceId: null,
    paidByCash: false,
    paymentMethodId: null
}

export interface RequestBasicInfo extends BasicInfo {
    sourceId?: string | null;
}

export const RequestBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().when('isOutgoing', {
        is: false,
        then: (schema) => schema.required("Currency is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
});

export interface OfferBasicInfo extends BasicInfo {
    bindingDeadline?: Date | null;
    requestId?: string | null;
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;
}

export const OfferBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().required("Currency is required."),
    bindingDeadline: yup.date().typeError("Please type valid date.").nullable(),
    requestId: yup.string().nullable(),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
});

export interface OrderBasicInfo extends BasicInfo {
    bindingDeadline?: Date | null;
    offerId?: string | null;
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;
}

export const OrderBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().required("Currency is required."),
    bindingDeadline: yup.date().typeError("Please type valid date.").nullable(),
    offerId: yup.string().nullable(),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
});

export interface OrderConfirmationBasicInfo extends BasicInfo {
    bindingDeadline?: Date | null;
    orderId?: string | null;
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;
}

export const OrderConfirmationBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().required("Currency is required."),
    bindingDeadline: yup.date().typeError("Please type valid date.").nullable(),
    orderId: yup.string().nullable(),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
});

export interface InvoiceBasicInfo extends BasicInfo {
    customInvoiceNumber?: string | null;
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;

    deliveryPeriodFrom: Date | null;
    deliveryPeriodTo: Date | null;

    periodFrom: Date | null;
    periodTo: Date | null;

    orderId?: string | null;
    orderConfirmationId?: string | null;

    paymentMethodId?: string | null;
    paidByCash: boolean;
}

const emptyToNull = (value: any, originalValue: any) =>
    originalValue === "" ? null : value;

export const InvoiceBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    customInvoiceNumber: yup.string().nullable(),
    currency: yup.string().required("Currency is required."),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
    deliveryPeriodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('deliveryPeriodTo'), "Delivery period from cannot be later than delivery period to.").nullable(),
    deliveryPeriodTo: yup.date().typeError("Please type valid date.").min(yup.ref('deliveryPeriodFrom'), "Delivery period to cannot be earlier than delivery period from.").nullable(),
    periodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('periodTo'), "Period from cannot be later than period to.").transform(emptyToNull)
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    periodTo: yup.date().typeError("Please type valid date.").min(yup.ref('periodFrom'), "Period to cannot be earlier than period from.").transform(emptyToNull)
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    orderId: yup.string().nullable(),
    orderConfirmationId: yup.string().nullable(),
    paymentMethodId: yup.string().nullable().required("Payment method is required."),
    paidByCash: yup.boolean()
});

export interface CreditNoteBasicInfo extends InvoiceBasicInfo {
    isFullCancellation: boolean;
}

export const CreditNoteBasicInfoRequestModelScheme = InvoiceBasicInfoRequestModelScheme.shape({
    paymentMethodId: yup.string().nullable().notRequired(),
    isFullCancellation: yup.boolean().required("Full cancellation status is required."),
});


export interface ReminderBasicInfo extends BasicInfo {
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;

    deliveryPeriodFrom: Date | null;
    deliveryPeriodTo: Date | null;

    periodFrom: Date;
    periodTo: Date;

    invoiceId?: string | null;
    documentTypeId: number;
}

export const ReminderBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().required("Currency is required."),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
    deliveryPeriodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('deliveryPeriodTo'), "Delivery period from cannot be later than delivery period to.").nullable(),
    deliveryPeriodTo: yup.date().typeError("Please type valid date.").min(yup.ref('deliveryPeriodFrom'), "Delivery period to cannot be earlier than delivery period from.").nullable(),
    periodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('periodTo'), "Period from cannot be later than period to.")
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    periodTo: yup.date().typeError("Please type valid date.").min(yup.ref('periodFrom'), "Period to cannot be earlier than period from.")
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    invoiceId: yup.string().nullable(),
    documentTypeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(1, "Type is required.").required("Document type is required.")
});

export interface DunningBasicInfo extends BasicInfo {
    paymentDays: number | null;
    discountDays: number | null;
    discount: number | null;

    deliveryPeriodFrom: Date | null;
    deliveryPeriodTo: Date | null;

    periodFrom: Date;
    periodTo: Date;

    invoiceId?: string | null;
    documentTypeId: number;
}

export const DunningBasicInfoRequestModelScheme = BasicInfoRequestModelScheme.shape({
    currency: yup.string().required("Currency is required."),
    paymentDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Payment days must be greater than or equal to 0.").max(36000, "Please type valid payment days.").integer("Payment days must be a whole number.").required("Payment days are required."),
    discountDays: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, "Discount days must be greater than or equal to 0.").max(36000, "Please type valid discount days.").integer("Discount days must be a whole number.").nullable(),
    discount: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0, 'Discount must be number between 0 and 100.').max(100, 'Discount must be number between 0 and 100.').nullable(),
    deliveryPeriodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('deliveryPeriodTo'), "Delivery period from cannot be later than delivery period to.").nullable(),
    deliveryPeriodTo: yup.date().typeError("Please type valid date.").min(yup.ref('deliveryPeriodFrom'), "Delivery period to cannot be earlier than delivery period from.").nullable(),
    periodFrom: yup.date().typeError("Please type valid date.").max(yup.ref('periodTo'), "Period from cannot be later than period to.")
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    periodTo: yup.date().typeError("Please type valid date.").min(yup.ref('periodFrom'), "Period to cannot be earlier than period from.")
        .when('isOutgoing', {
            is: (isOutgoing: boolean) => isOutgoing === true,
            then: (schema) => schema.required("Period is required."),
            otherwise: (schema) => schema.notRequired()
        }).nullable(),
    invoiceId: yup.string().nullable(),
    documentTypeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(1, "Type is required.").required("Document type is required."),
});

export const schemaMap = {
    [FinanceDocumentType.Request]: RequestBasicInfoRequestModelScheme,
    [FinanceDocumentType.Offer]: OfferBasicInfoRequestModelScheme,
    [FinanceDocumentType.Order]: OrderBasicInfoRequestModelScheme,
    [FinanceDocumentType.OrderConfirmation]: OrderConfirmationBasicInfoRequestModelScheme,
    [FinanceDocumentType.Invoice]: InvoiceBasicInfoRequestModelScheme,
    [FinanceDocumentType.CreditNote]: CreditNoteBasicInfoRequestModelScheme,
    [FinanceDocumentType.Reminder]: ReminderBasicInfoRequestModelScheme,
    [FinanceDocumentType.Dunning]: DunningBasicInfoRequestModelScheme,
    [FinanceDocumentType.All]: BasicInfoRequestModelScheme,
};

//#endregion

//#region ProcedureInfo
export interface ProcedureFilesRequest {
    procedureId: string;
    files: ProcedureInfoFile[]
}

export interface ProcedureFilesResponse {
    message: string;
    files: ProcedureInfoFile[]
}

export interface ProcedureInfoFile {
    parentFileId?: string | null
    id?: string | null
    fileName: string;
    description: string | null;
    fileBase64: string;
    isMain: boolean;
    createdAt: Date | null;
    createdBy: string | null;
    editedAt: Date | null;
    editedBy: string | null;
    position: string;
    additionalFiles?: ProcedureInfoFile[]
}

export interface UpdateProcedureInfoRequest {
    financeProcedure: ProcedureInfo,
    documentTypeId?: number;
    documentId?: string;
}

export interface UpdateProcedureInfoResponse {
    financeProcedure: ProcedureInfo,
    documentStatusId: number
}

export interface ProcedureInfo {
    docType: number;
    projectId?: string | null;
    noCostCenter?: boolean | null;
    projectName?: string | null;
    partOfExistingProcedure: boolean;
    procedureTypeId?: number | null;
    name?: string | null;
    procedureId?: string | null;
    contractStartDate?: Date | null;
    contractEndDate?: Date | null;
    contractTypeId?: number | null;
    contractNumber?: string | null;
    paymentIntervalId?: number | null;
    invoiceEmail?: string | null;
    ourCostcenterEmail?: string | null;
    note?: string | null;
    noticePeriod?: number | null;
    partnerProjectName?: string | null;
    amountPerHour?: number | null;
    totalAmount?: number | null;
    plannedHours?: number | null;
    currancy?: string | null;
    files: ProcedureInfoFile[] | [];
    relation?: string | null;
    extensionId?: string | null;
    categoryId?: string | null;
    paymentMethodId?: string | null;
    clientNumber?: string | null;

    tenantId: string;
    tenantLocationId: string;
    tenantResponsiblePersonId: string;
    tenantLocationBankDataId: string | null;

    partnerId: string;
    partnerLocationId: string;
    contactId?: string | null;
    partnerBankDataId: string | null;

    securityDeposit?: boolean | null,
    depositPercent?: number | null
}

export const initialProcedureInfo: ProcedureInfo = {
    projectId: null,
    noCostCenter: false,
    projectName: null,
    partOfExistingProcedure: false,
    name: null,
    procedureId: null,
    contractStartDate: null,
    contractEndDate: null,
    contractTypeId: null,
    contractNumber: null,
    paymentIntervalId: null,
    invoiceEmail: null,
    ourCostcenterEmail: null,
    note: null,
    noticePeriod: null,
    partnerProjectName: null,
    amountPerHour: null,
    totalAmount: null,
    plannedHours: null,
    currancy: null,
    files: [],
    tenantId: "",
    tenantLocationId: "",
    tenantResponsiblePersonId: "",
    tenantLocationBankDataId: null,

    partnerId: "",
    partnerLocationId: "",
    contactId: null,
    partnerBankDataId: null,
    procedureTypeId: 0,
    docType: 0,

    securityDeposit: false,
    depositPercent: null
};

export interface ProcedureTabData {
    procedureInfo: ProcedureInfo;
    // procedureFileForDownload: ProcedureInfoFile | null;
}

export const initialProcedureTabData: ProcedureTabData = {
    procedureInfo: initialProcedureInfo,
    // procedureFileForDownload: null
}


const isOutgoingFromForm = (docType: number, procedureTypeId: number | null): boolean => {
    const isRequestOrOrder = docType === 1 || docType === 3;
    const isClient = procedureTypeId === ProcedureType.Client;
    const isSupplier = procedureTypeId === ProcedureType.Supplier;
    return !((isRequestOrOrder && isClient) || (!isRequestOrOrder && isSupplier));
};

export const ProcedureInfoDataRequestModelSchema = yup.object().shape({
    docType: yup.number().transform((value) => Number.isNaN(value) ? null : value).defined(),
    projectId: yup.string().when('noCostCenter', {
        is: false,
        then: (schema) => schema.nonNullable().required("Profit/Cost center is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    noCostCenter: yup.boolean().nullable(),
    projectName: yup.string().when('noCostCenter', {
        is: true,
        then: (schema) => schema.nonNullable().required("Profit/Cost center name is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    partOfExistingProcedure: yup.boolean().defined().required(),
    procedureTypeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    name: yup.string().noLeadingSpace().when('partOfExistingProcedure', {
        is: false,
        then: (schema) => schema.required("Procedure name is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    procedureId: yup.string().when('partOfExistingProcedure', {
        is: true,
        then: (schema) => schema.required("Procedure is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    contractStartDate: yup.date().typeError("Please type valid date.").nullable()
        .when(['docType', 'procedureTypeId'], {
            is: (docType: number, procedureTypeId: number | null) => isOutgoingFromForm(docType, procedureTypeId),
            then: (schema) => schema.required("Start date is required."),
            otherwise: (schema) => schema.notRequired(),
        })
        .test(
            "start-before-end",
            "Start date cannot be later than end date.",
            function (value) {
                const { contractEndDate } = this.parent;
                if (value && contractEndDate) {
                    return value <= contractEndDate;
                }
                return true;
            }
        ),
    contractEndDate: yup.date().typeError("Please type valid date.").nullable()
        .when(['docType', 'procedureTypeId'], {
            is: (docType: number, procedureTypeId: number | null) => isOutgoingFromForm(docType, procedureTypeId),
            then: (schema) => schema.required("End date is required."),
            otherwise: (schema) => schema.notRequired(),
        })
        .test(
            "end-after-start",
            "End date cannot be earlier than start date.",
            function (value) {
                const { contractStartDate } = this.parent;
                if (value && contractStartDate) {
                    return value >= contractStartDate;
                }
                return true;
            }
        ),
    contractTypeId: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable()
        .when(['docType', 'procedureTypeId'], {
            is: (docType: number, procedureTypeId: number | null) => isOutgoingFromForm(docType, procedureTypeId),
            then: (schema) => schema.required("Contract type is required."),
            otherwise: (schema) => schema.notRequired(),
        }),
    contractType: yup.string().nullable(),
    contractNumber: yup.string().nullable(),
    paymentIntervalId: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    invoiceEmail: yup.string().nullable(),
    ourCostcenterEmail: yup.string().nullable(),
    note: yup.string().nullable(),
    noticePeriod: yup.number().transform((value) => Number.isNaN(value) ? null : value).min(0).nullable(),
    partnerProjectName: yup.string().nullable(),
    amountPerHour: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    totalAmount: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    plannedHours: yup.number().transform((value) => Number.isNaN(value) ? null : value).nullable(),
    currancy: yup.string().nullable(),
    files: yup.array().defined(),
    extensionId: yup.string().nullable(),
    categoryId: yup.string().nullable(),
    paymentMethodId: yup.string().nullable(),
    relation: yup.string().nullable(),

    tenantId: yup.string().required("Our company is required."),
    tenantLocationId: yup.string().required("Our location is required."),
    tenantResponsiblePersonId: yup.string().required("Our responsible person is required."),
    // tenantLocationBankDataId: yup.string().nullable().defined(),
    tenantLocationBankDataId: yup.string().when(['docType', 'procedureTypeId'], {
        is: (docType: number, procedureTypeId: number) => docType > 4 && procedureTypeId === ProcedureType.Client,
        then: (schema) => schema.required("Our bank account is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable().defined(),

    partnerId: yup.string().required("Company is required."),
    partnerLocationId: yup.string().required("Location is required."),
    contactId: yup.string().nullable(),
    partnerBankDataId: yup.string().when(['docType', 'procedureTypeId'], {
        is: (docType: number, procedureTypeId: number) => docType > 4 && procedureTypeId === ProcedureType.Supplier,
        then: (schema) => schema.required("Supplier bank account is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable().defined(),

    securityDeposit: yup.boolean().nullable(),
    depositPercent: yup
        .number()
        .transform((value, originalValue) =>
            originalValue === "" ? undefined : Number.isNaN(value) ? null : value
        )
        .nullable()
        .when("securityDeposit", {
            is: true,
            then: (schema) => schema
                .required("Deposit percent is required.")
                .min(1, "Deposit percent must be between 1 and 100.")
                .max(100, "Deposit percent must be between 1 and 100."),
            otherwise: (schema) => schema
                .nullable()
                .notRequired()
                .oneOf([null, 0, undefined], "Deposit percent must be null, 0, or undefined when security deposit is false.")
        }),
});

//#endregion

//#region Positions

export interface Position {
    id: string;
    articleId?: string | null;
    name: string;
    positionNumber?: string | null;
    quantity?: number | null;
    unitId?: number | null;
    unitName?: string | null;
    material?: number | null;
    wage?: number | null;
    unitPrice?: number | null;
    discount?: number | null;
    groupId: string | null;
    isGroup: boolean;
    tax?: number | null;
    subRows?: Position[] | null,
    order: number;
    net: number | null;
    gross: number | null;
    isOneTimeCost: boolean;
    personnelHours?: number | null;
    accountingNumberId: string | null;
    inventoryNumber?: string | null;
    comment?: string | null;
    description?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
};

export interface GetPositionCreateDataResponse {
    articles: TranslationList[] | [],
}

export interface UpdatePositionDTO {
    oldGroupId: string | null,
    position: Position
}

export interface ReminderPosition {
    id: string;
    name: string;
    positionNumber?: string | null;
    order: number;
    invoiceDate: Date;
    invoiceAmount?: number | null;
    paymentAmount?: number | null;
};

export interface ReminderPositionPayload {
    invoiceDate: Date | null;
    invoiceNumber: string | null;
}

export const ReminderPositionModelScheme = yup.object().shape({
    id: yup.string().defined(),
    name: yup.string().noLeadingSpace().required("Position name is required."),
    positionNumber: yup.string().nullable(),
    order: yup.number().defined(),
    invoiceDate: yup.date().typeError("Please type valid date.").min(dayjs().add(-100, 'year').toDate(), "Please insert valid date.")
        .max(dayjs().endOf('day').toDate(), "The entered date cannot be later than today.")
        .required('Invoice date is required.'),
    invoiceAmount: yup.number().localeNumber().nullable().typeError("Invoice amount must be a valid number.")
        .test("required-invoiceAmount", "Invoice amount is required.", (value) => value !== null && value !== undefined).min(0, "Invoice amount must be at least 0."),
    paymentAmount: yup.number().localeNumber().nullable().typeError("Payment amount must be a valid number.")
        .test("required-paymentAmount", "Payment amount is required.", (value) => value !== null && value !== undefined).min(0, "Payment amount must be at least 0."),
});

export interface UpdatePositions {
    entityId: string,
    positions: Position[],
    editedPositionId: string | null,
    action: BatchUpdateActions
}

export interface UpdatePositionResponse {
    positions: Position[],
    documentStatusId: number,
    message: string
}

export interface UpdateReminderPositionsRequest {
    entityId: string,
    reminderPositions: ReminderPosition[],
    editedPositionId: string | null,
    action: BatchUpdateActions
}

export interface UpdateReminderPositionResponse {
    reminderPositions: ReminderPosition[],
    message: string
}

export interface PositionsTabData {
    position: Position,
    positions: Position[],
    lastValidPositions: Position[],
    updatePositionsInDb: boolean,
    positionGroups: ListModel<string>[],
    reminderPosition: ReminderPosition,
    reminderPositions: ReminderPosition[],
    lastValidReminderPositions: ReminderPosition[],
    updateReminderPositionsInDb: boolean
}

export const PositionModelScheme = yup.object().shape({
    id: yup.string().defined(),
    name: yup.string().noLeadingSpace().required("Position name is required."),
    articleId: yup.string().nullable(),
    positionNumber: yup.string().nullable(),
    subRows: yup.array().nullable(),
    isGroup: yup.boolean().defined(),
    groupId: yup.string().defined().nullable(),
    order: yup.number().defined(),
    quantity: yup.number().localeNumber().typeError("Quantity must be a valid number.").positive('The quantity must be a positive number greater than or equal to 0.').min(0, "The quantity must be a positive number greater than or equal to 0.").when('isGroup', {
        is: false,
        then: (schema) => schema.required("Quantity is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    unitId: yup.number().when('isGroup', {
        is: false,
        then: (schema) => schema.min(1, 'Unit is required.').required("Unit is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    unitName: yup.string().notRequired(),
    material: yup.number().localeNumber().typeError("Material price must be a valid number.").when('isGroup', {
        is: false,
        then: (schema) => schema.required("Material price is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    wage: yup.number().localeNumber().typeError("Wage must be a valid number.")/* .positive('Wage must be a positive number greater than or equal to 0.').min(0, "Wage must be a positive number greater than or equal to 0.") */.when('isGroup', {
        is: false,
        then: (schema) => schema.nonNullable().required("Wage is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    tax: yup.number().localeNumber().typeError("Tax must be a valid number.").positive('Tax must be positive number between 0 and 100.').min(0, "Tax must be positive number between 0 and 100.").max(100).when('isGroup', {
        is: false,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    discount: yup.number().localeNumber().typeError("Price correction must be a valid number.")/* .positive('Discount must be positive number between 0 and 100.').min(0, "Discount must be positive number between 0 and 100.") */.max(100, "The discount cannot be more than 100.").when('isGroup', {
        is: false,
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    personnelHours: yup
        .number()
        .localeNumber()
        .typeError("Personnel hours must be a valid number.")
        .positive("Personnel hours must be positive number.")
        .min(0, "Personnel hours must be positive number.")
        .notRequired().nullable(),
    net: yup.number().defined().nullable(),
    gross: yup.number().defined().nullable(),
    isOneTimeCost: yup.boolean().defined(),
    accountingNumberId: yup.string().defined().nullable(),
    inventoryNumber: yup.string().nullable(),
    comment: yup.string().noLeadingSpace().nullable(),
    description: yup.string().nullable(),
    startDate: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("start-before-end", "Start date cannot be later than end date.", function (value) {
            const { endDate } = this.parent;
            if (!value || !endDate) return true;
            return value <= endDate;
        }),

    endDate: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("end-after-start", "End date cannot be earlier than start date.", function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return value >= startDate;
        }),
}) as yup.ObjectSchema<Position>;

export const RequestPositionModelScheme = yup.object().shape({
    id: yup.string().defined(),
    name: yup.string().noLeadingSpace().required("Name is required."),
    articleId: yup.string().nullable(),
    positionNumber: yup.string().noLeadingSpace().nullable(),
    subRows: yup.array().nullable(),
    isGroup: yup.boolean().defined(),
    groupId: yup.string().defined().nullable(),
    order: yup.number().defined(),
    quantity: yup.number().localeNumber().typeError("Quantity must be a valid number.").positive('The quantity must be a positive number greater than or equal to 0.').min(0, "The quantity must be a positive number greater than or equal to 0.").when('isGroup', {
        is: false,
        then: (schema) => schema.required("Quantity is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    unitId: yup.number().when('isGroup', {
        is: false,
        then: (schema) => schema.min(1, 'Unit is required.').required("Unit is required."),
        otherwise: (schema) => schema.notRequired()
    }).nullable(),
    unitName: yup.string().notRequired(),
    material: yup.number()/* .positive('Material price must be a positive number greater than or equal to 0.').min(0, "Material price must be a positive number greater than or equal to 0.") */.notRequired().nullable(),
    wage: yup.number()/* .positive('Wage must be a positive number greater than or equal to 0.').min(0, "Wage must be a positive number greater than or equal to 0.") */.notRequired().nullable(),
    tax: yup.number().positive('Tax must be positive number between 0 and 100.').min(0, "Tax must be positive number between 0 and 100.").max(100, "Tax must be positive number between 0 and 100.").notRequired().nullable(),
    discount: yup.number()/* .positive('Discount must be positive number between 0 and 100.').min(0, "Discount must be positive number between 0 and 100.") */.max(100, "Discount must be positive number between 0 and 100.").notRequired().nullable(),
    net: yup.number().defined().nullable(),
    gross: yup.number().defined().nullable(),
    isOneTimeCost: yup.boolean().defined(),
    personnelHours: yup.number().localeNumber().typeError("Personnel hours must be a valid number.").positive("Personnel hours must be positive.").notRequired().nullable(),
    accountingNumberId: yup.string().defined().nullable(),
    inventoryNumber: yup.string().nullable(),
    comment: yup.string().noLeadingSpace().nullable(),
    description: yup.string().nullable(),
    startDate: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("start-before-end", "Start date cannot be later than end date.", function (value) {
            const { endDate } = this.parent;
            if (!value || !endDate) return true;
            return value <= endDate;
        }),

    endDate: yup
        .date()
        .nullable()
        .typeError("Please type valid date.")
        .test("end-after-start", "End date cannot be earlier than start date.", function (value) {
            const { startDate } = this.parent;
            if (!value || !startDate) return true;
            return value >= startDate;
        }),
})

export const InitPositionModel: Position = {
    id: "",
    name: "",
    articleId: null,
    positionNumber: "",
    unitId: null,
    // Quantity, material and wage are the three required price drivers, so they all start
    // pre-filled: a position normally charges either labour or material, and quantity is
    // normally one. Leaving one of them empty made the same kind of field behave differently.
    material: 0,
    wage: 0,
    quantity: 1,
    discount: null,
    tax: null,
    isGroup: true,
    groupId: null,
    net: null,
    gross: null,
    order: 1,
    subRows: [],
    isOneTimeCost: false,
    personnelHours: null,
    accountingNumberId: null,
    inventoryNumber: null,
    comment: null,
    description: null,
    startDate: null,
    endDate: null
};

export const InitReminderPositionModel: ReminderPosition = {
    id: "",
    name: "",
    positionNumber: null,
    invoiceDate: new Date(),
    invoiceAmount: null,
    paymentAmount: null,
    order: 1
};


export const initPositionsTabData: PositionsTabData = {
    position: InitPositionModel,
    positions: [],
    lastValidPositions: [],
    updatePositionsInDb: false,
    positionGroups: [],
    reminderPosition: InitReminderPositionModel,
    reminderPositions: [],
    lastValidReminderPositions: [],
    updateReminderPositionsInDb: false
}

export interface InvoceAndPaymentSumAmountResponse {
    currency: string,
    invoiceAmount: number,
    paidAmount: number,
    invoiceId: string | null;
    invoiceDate: Date | null;
    invoiceNumber: string | null;
    tenantAbbreviation: string;
}

//#endregion

//#region ProjectData

export interface GetProjectDataResponse {
    projectBasicInfo: ProjectBasicInfo | null,
    existingProcedures: ListModelWithIntValue<string>[] | [],
    projectTemplates: GroupListModel<string>[] | [],
}

export interface ProjectBasicInfo {
    projectId: string,
    name: string,
    longName: string,
    number: string,
    projectEmail?: string | null,
}

export interface ProjectData extends GetProjectDataResponse {
    projectTemplates: GroupListModel<string>[] | [],
}

export const initProjectData: ProjectData = {
    projectTemplates: [],
    projectBasicInfo: null,
    existingProcedures: []
}

//#endregion

//#region Textblocks

export interface TextBlockTabData {
    textblockForEdit: TextBlock | null
    selectedTextblocks: TextBlock[] | []
    lastValidData: TextBlock[] | []
}

export interface TextBlock {
    id: string;
    textBlockId?: string | null;
    name: string;
    positionId: number;
    orderInPosition: number;
    defaultText: string;
    defaultLanguage?: string | null;
    textTranslations?: TranslationUDF[] | null;
}

export interface UpdateTextBlocks {
    entityId: string,
    textBlocks: TextBlock[],
    editedTextBlockId: string | null,
    action: BatchUpdateActions
}

export interface UpdateTextBlocksResponse {
    textBlocks: TextBlock[],
    documentStatusId: number,
    message: string
}

export const TextblockEditScheme = yup.object().shape({
    name: yup.string().noLeadingSpace().nullable(),
    defaultText: yup.string().required("English text is required."),
    textTranslations: yup.array().min(0, '').of(TranslationUDFScheme).nullable(),
})

const stripHtml = (html: string) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent?.trim() || '';
};

export const TextblockModelScheme = yup.object().shape({
    id: yup.string().defined(),
    textBlockId: yup.string().nullable(),
    name: yup.string().noLeadingSpace().required("Name is required."),
    positionId: yup.number().required("Position is required.").typeError("Position is required.").positive("Position is required."),
    orderInPosition: yup.number().required("Order in position is required."),
    defaultText: yup
        .string()
        .test(
            "rich-text-required",
            "Text is required.",
            (value) => !!stripHtml(value || '')
        ).required(),
    textTranslations: yup.array().min(0, '').of(TranslationUDFScheme).nullable(),
})

//#endregion Textblocks

//#region Attachments

export interface AttachmentsTabData {
    attachmentToDownload: DownloadFileResponse | null
    attachments: Attachment[]
}

export interface Attachment {
    id?: string | null
    fileName: string;
    isMainDocument: boolean;
    isShownOnPDF?: boolean;
    fileOrder?: number;
    fileBase64: string | null;
    description?: string | null;
    createdBy: string | null;
    createdAt: Date | null;
}

export interface AttachmentRequest {
    id: string;
    files: Attachment[];
}

export interface DownloadFileResponse {
    fileBase64: string;
    fileName: string;
}

export interface FileUplaodResponse {
    message: string;
    files: Attachment[];
    documentStatusId: number;
}

export interface FileDeleteResponse {
    message: string;
    documentStatusId: number;
}

export interface UpdateFileDescriptionRequest {
    documentTypeId: number;
    fileId: string;
    description: string;
    isMainDocument: boolean;
    isShownOnPDF?: boolean;
}

export interface ReorderAttachmentsRequest {
    documentTypeId: number;
    documentId: string;
    attachmentOrder: Record<string, number>;
}

export const AttachmentRequestModelScheme = yup.object().shape({
    fileName: yup.string().required("Name is required."),
    isMainDocument: yup.boolean().defined(),
    fileBase64: yup.string().defined().nullable(),
    description: yup.string().nullable(),
    createdBy: yup.string().defined().nullable(),
    createdAt: yup.date().defined().nullable(),
})

export const initAttachmentsTabData: AttachmentsTabData = {
    attachmentToDownload: null,
    attachments: []
}

//#endregion

//#region PaymentData

export interface PaymentDataRequest {
    invoiceId: string | null;
    purposeOfTransaction: string;
    formOfPayment: string;
    purposeCode: string;
    chargeModel?: number | null;
    chargeReference: string | null;
}

export const initialPaymentData: PaymentDataRequest = {
    invoiceId: "",
    purposeOfTransaction: "",
    formOfPayment: "",
    purposeCode: "",
    chargeModel: null,
    chargeReference: ""
}

export const PaymentDataRequestModelScheme = yup.object().shape({
    invoiceId: yup.string().defined().nullable(),
    purposeOfTransaction: yup.string()
        .min(1, "Payment purpose is required.")
        .max(36, "Payment purpose can have a maximum of 36 characters.")
        .required("Payment purpose is required."),
    formOfPayment: yup.string()
        .min(1, "Form of payment must consist of only 1 character.")
        .max(1, "Form of payment must consist of only 1 character.")
        .required("Form of payment is required."),
    purposeCode: yup.string()
        .min(1, "Payment code is required.")
        .max(2, "Payment code can have a maximum of 2 characters.")
        .required("Payment code is required."),
    chargeModel: yup.number()
        .transform((value) => Number.isNaN(value) ? null : value)
        .min(10, 'Payment model must be exactly 2 digits.')
        .max(99, 'Payment model must be exactly 2 digits.')
        .nullable(),
    chargeReference: yup.string().defined().nullable(),
})

export interface PaymentDataResponse {
    purposeOfTransaction: string;
    formOfPayment: string;
    purposeCode: string;
    chargeModel?: number | null;
    chargeReference: string | null;
}

export interface PaymentDataResponseWrapper {
    invoicePaymentData: PaymentDataResponse;
    message: string;
}

export interface ExportFileDataResponseTab {
    exportFile: ExportFileResponse | null
    exportFileData: ExportFileDataResponse | null
}

export const initialExportFileDataResponse: ExportFileDataResponseTab = {
    exportFile: null,
    exportFileData: null
}

export interface ExportFileResponse {
    fileName: string;
    fileBase64: string;
}

export interface ExportFileDataResponse {
    valueDate: Date | null;
    senderName: string;
    senderCity: string;
    exportFormats: ListModel<number>[];
    paymentTypes: ListModel<number>[];
    chargeBearer: ListModel<number>[];
    tenantLocations: ListModelWithDetail<string>[];
    paymentDataForExport?: PaymentDataForTXTExport[] | PaymentDataForXMLExport[] | [];
}

export interface ExportFileDataRequest {
    ids: string[]
}

//#region TXT Export

export interface PaymentDataForTXTExport {
    id?: string | null;
    invoiceId: string; //TXT* XML*
    recipientBankData: string; //TXT* XML*
    recipientName: string; //TXT* XML*
    recipientAddress?: string | null; //TXT XML*
    recipientCity?: string | null; //TXT XML*
    chargeModel?: number | null; //TXT
    purposeOfTransaction: string; //TXT* 
    formOfPayment: string; //TXT
    purposeCode: string; //TXT
    chargeReference?: string | null; //TXT
    invoiceTotalBruto: number; //TXT* XML*
    valueDate: Date; //TXT*
}

export const initialPaymentDataForTXTExport: PaymentDataForTXTExport = {
    invoiceId: '',
    recipientBankData: '',
    recipientName: '',
    recipientAddress: '',
    recipientCity: '',
    chargeModel: null,
    purposeOfTransaction: '',
    formOfPayment: '',
    purposeCode: '',
    chargeReference: '',
    invoiceTotalBruto: 0,
    valueDate: nullDate
}

export const getPaymentDataForTXTExportSchema = (isErstePoland: boolean = false) => yup.object().shape({
    id: yup.string().nullable(),
    invoiceId: yup.string().required('Invoice is required.'),
    recipientBankData: yup.string().required('Receiver bank account is required.'),
    recipientName: yup.string().required('Receiver name is required.'),
    recipientAddress: yup.string().nullable(),
    recipientCity: yup.string().nullable(),
    chargeModel: yup.number()
        .transform((value) => Number.isNaN(value) ? null : value)
        .min(10, 'Payment model must be exactly 2 digits.')
        .max(99, 'Payment model must be exactly 2 digits.')
        .nullable(),
    purposeOfTransaction: isErstePoland
        ? yup.string().nullable()
        : yup.string().min(1, 'Payment purpose is required.')
            .max(36, 'Payment purpose can have a maximum of 36 characters.')
            .required('Payment purpose is required.'),
    formOfPayment: isErstePoland
        ? yup.string().nullable()
        : yup.string().min(1, 'Form of payment must consist of only 1 character.')
            .max(1, 'Form of payment must consist of only 1 character.')
            .required('Form of payment is required.'),
    purposeCode: isErstePoland
        ? yup.string().nullable()
        // Exactly two digits, not "up to two": the fixed-width Erste and Halcom writers slice
        // this field with Substring(0, 2), so a single character makes the export fail.
        : yup.string()
            .required('Payment code is required.')
            .matches(/^[0-9]{2}$/, 'Payment code must be exactly 2 digits.'),
    chargeReference: yup.string().nullable(),
    invoiceTotalBruto: yup.number().transform((value) => Number.isNaN(value) ? null : value).required('Payment amount is required.'),
    valueDate: yup.date().required('Value date is required.').typeError("Please type valid date.").min(dayjs().startOf('day').toDate(), "The entered date cannot be earlier than today."),
});

export const PaymentDataForTXTExportSchema = getPaymentDataForTXTExportSchema(false);

export interface ExportTXTFileRequest {
    tenantLocationId: string; //only for validation
    senderBankDataId: string; //TXT* XML*
    senderName: string; //TXT* XML*
    senderCity?: string | null; //TXT
    exportFormat: number; //TXT* XML*
    paymentDataForExport: PaymentDataForTXTExport[]
}

export const initialExportTXTFileRequest: ExportTXTFileRequest = {
    tenantLocationId: '',
    senderBankDataId: '',
    senderName: '',
    exportFormat: -1,
    paymentDataForExport: [],
}

export const getExportTXTFileRequestSchema = (isErstePoland: boolean = false) => yup.object().shape({
    tenantLocationId: yup.string().required('Sender location is required.'),
    senderBankDataId: yup.string().required('Sender bank account is required.'),
    senderName: yup.string().required('Sender name is required.'),
    senderCity: yup.string().nullable(),
    exportFormat: yup.number().transform((value) => Number.isNaN(value) ? null : value).required('Export format is required.').moreThan(0, 'Export format is required.'),
    paymentDataForExport: yup.array().of(getPaymentDataForTXTExportSchema(isErstePoland)).required('Payment data for export is required.').min(1, 'Payment data for export is required.'),
});

export const ExportTXTFileRequestSchema = getExportTXTFileRequestSchema(false);

//#endregion

//#region XML Export

/**
 * How a recipient account is written into the pain.001 file. Countries such as the USA have no IBAN
 * scheme, so their accounts go into <Othr><Id> rather than <IBAN>.
 */
export type AccountType = 'IBAN' | 'OTHER';

export const AccountTypes: { Iban: AccountType, Other: AccountType } = {
    Iban: 'IBAN',
    Other: 'OTHER',
};

export interface PaymentDataForXMLExport {
    id?: string | null;
    invoiceId: string; //TXT* XML*
    invoiceNumber: string; //XML*
    accountType: AccountType; //XML*
    recipientBankData: string; //TXT* XML*
    recipientBICSwift: string; //XML*
    recipientName: string; //TXT* XML*
    recipientAddress: string; //TXT XML*
    recipientZipCode?: string | null; //XML
    recipientCity: string; //TXT XML*
    recipientCountry: string; //XML*
    invoiceTotalBruto: number; //TXT* XML*
}

export const initialPaymentDataForXMLExport: PaymentDataForXMLExport = {
    id: null,
    invoiceId: '',
    invoiceNumber: '',
    accountType: AccountTypes.Iban,
    recipientBankData: '',
    recipientBICSwift: '',
    recipientName: '',
    recipientAddress: '',
    recipientZipCode: '',
    recipientCity: '',
    recipientCountry: '',
    invoiceTotalBruto: 0,
};

/** The charge bearers the foreign transfer schema allows. SLEV is SEPA only. */
export const InternationalChargeBearers = ['DEBT', 'CRED', 'SHAR'];

function isSepaPaymentType(paymentType?: number | null) {
    return paymentType === XmlPaymentType.AustrianStandard || paymentType === XmlPaymentType.GermanStandard;
}

/**
 * The rules follow the payment type, as SepaPaymentRowValidator and InternationalPaymentRowValidator
 * do on the backend. Without a payment type only the shared rules apply.
 */
export const getPaymentDataForXMLExportSchema = (paymentType?: number | null) => yup.object().shape({
    id: yup.string().nullable(),
    invoiceId: yup.string().required('Invoice is required.'),
    invoiceNumber: yup.string().required('Invoice number is required.'),
    accountType: yup.string().oneOf([AccountTypes.Iban, AccountTypes.Other], 'Account type is required.').required('Account type is required.')
        // A SEPA pain.001 has no <Othr> account: anything but an IBAN fails the bank's schema.
        .test('sepaIbanOnly', 'SEPA transfers can only go to an IBAN. Use the International payment type for an account without one.',
            (value) => !isSepaPaymentType(paymentType) || value !== AccountTypes.Other),
    // Mirrors XmlPaymentRowValidator on the backend: a full IBAN check when the row is an IBAN,
    // and only a length sanity check otherwise, because domestic account formats vary too much
    // between countries to validate centrally.
    recipientBankData: yup.string()
        .required('Receiver bank account is required.')
        .when('accountType', {
            is: AccountTypes.Iban,
            then: (schema) => schema.test('iban', 'The recipient IBAN is not valid.', (value) => isValidIban(value)),
            otherwise: (schema) => schema.test(
                'accountNumberLength',
                'The recipient account number must not exceed 34 characters.',
                (value) => normalizeIban(value).length <= 34,
            ),
        }),
    // The 00000000 placeholder means "no BIC" and is left to the backend, like a blank value.
    recipientBICSwift: yup.string().required('Receiver SWIFT/BIC is required.')
        .test('bic', 'The recipient BIC is not valid.', (value) => !isBicProvided(value) || isValidBic(value)),
    recipientName: yup.string().required('Receiver name is required.'),
    recipientAddress: yup.string().required('Receiver address is required.'),
    recipientZipCode: yup.string().nullable(),
    recipientCity: yup.string().required('Receiver city is required.'),
    // pain.001 <Ctry> is an ISO 3166-1 alpha-2 code, so "Srbija" or "USA" is rejected by the bank
    // just as surely as the "98" this field used to be prefilled with.
    recipientCountry: yup.string()
        .required('Receiver country is required.')
        .matches(/^[A-Za-z]{2}$/, 'Receiver country must be a 2-letter country code.'),
    // Both pain.001 schemas reject 0.00, and the amount is rounded to cents before it is written.
    invoiceTotalBruto: yup.number().transform((value) => Number.isNaN(value) ? null : value).required('Payment amount is required.')
        .test('minAmount', 'The amount must be at least 0.01.', (value) => value == null || Math.round(value * 100) / 100 >= 0.01),
});

export const PaymentDataForXMLExportSchema = getPaymentDataForXMLExportSchema();

export interface ExportXMLFileRequest {
    tenantLocationId: string; //only for validation
    senderBankDataId: string; //TXT* XML*
    senderName: string; //TXT* XML*
    instructionId: string; //XML*
    exportFormat: number; //TXT* XML*
    chargeBearer: string; //XML*
    paymentType: number; //XML*
    paymentDataForExport: PaymentDataForXMLExport[]
}

export const initialExportXMLFileRequest: ExportXMLFileRequest = {
    tenantLocationId: '',
    senderBankDataId: '',
    senderName: '',
    exportFormat: 0,
    paymentDataForExport: [],
    instructionId: '',
    chargeBearer: '',
    paymentType: -1
}

export const ExportXMLFileRequestSchema = yup.object().shape({
    tenantLocationId: yup.string().required('Sender location is required.'),
    senderBankDataId: yup.string().required('Sender bank account is required.'),
    senderName: yup.string().required('Sender name is required.'),
    instructionId: yup.string().required('Instruction ID is required.'),
    exportFormat: yup.number().transform((value) => Number.isNaN(value) ? null : value).required('Export format is required.').moreThan(0, 'Export format is required.'),
    // SEPA files always say SLEV whatever was chosen, but the foreign transfer schema does not allow it.
    chargeBearer: yup.string().required('Charge bearer is required.')
        .when('paymentType', {
            is: XmlPaymentType.International,
            then: (schema) => schema.test('internationalChargeBearer', 'International transfers need the charge bearer DEBT, CRED or SHAR.',
                (value) => InternationalChargeBearers.includes((value ?? '').trim().toUpperCase())),
        }),
    paymentType: yup.number().transform((value) => Number.isNaN(value) ? null : value).required('Payment type is required.').moreThan(0, 'Payment type is required.'),
    paymentDataForExport: yup.array()
        .when('paymentType', ([paymentType], schema) => schema.of(getPaymentDataForXMLExportSchema(paymentType)))
        .required('Payment data for export is required.').min(1, 'Payment data for export is required.'),
});

//#endregion

//#region Pdf
export interface PdfTabData {
    additionalPdfData: AdditionalPdfDataModel,
    documentName: string,
    isCreatePdfProcessing: boolean,
    isFirstPdfCreated: boolean,
    isPdfDataReady: boolean,
    documentFiles: DocumentPdfModel[],
    // documentToDownload: DownloadDocumentResponse | null,
    isCostCenter: boolean,
}

export const initPdfTabData: PdfTabData = {
    additionalPdfData: AdditionalPdfDataModelInit,
    documentName: "",
    isCreatePdfProcessing: false,
    isFirstPdfCreated: false,
    isPdfDataReady: false,
    documentFiles: [],
    // documentToDownload: null,
    isCostCenter: true
}

export interface CreateDocumentNumberAndDateRequest {
    documentId: string,
    documentType: string,
    language: string,
    secondaryLanguage: string | null
}

export interface CreateDocumentNumberAndDateResponse {
    documentNumber: number,
    documentDate: Date,
    documentName: string,
    clientNumber: string,
    customInvoiceNumber?: string | null
}

export interface CreatePdfRequest {
    documentId: string,
    name: string,
    content: string,
    language: string,
    secondaryLanguage: string | null
}

export interface CreatePdfResponse {
    message: string,
    documents: DocumentPdfModel[]
}

export interface DocumentPdfModel {
    id: string,
    fileName: string,
    version: number,
    createdBy: string,
    createdAt: Date,
}

export interface DownloadDocumentResponse {
    name: string,
    content: string
}

export interface DownloadDocumentRequest {
    documentId: string,
    documentTypeId: string
}

//#endregion

export interface SetToApprovalReminderRequest {
    id: string,
    isDunning: boolean
}

//#region Reference data
export interface SourceInfo {
    isSourceDropdownLoading: boolean,
    newSourceId: string | null;
}

export interface CategoryInfo {
    isCategoryDropdownLoading: boolean,
    newCategoryId: string | null;
}

export interface PaymentMethodInfo {
    isPaymentMethodDropdownLoading: boolean,
    newPaymentMethodId: string | null;
}
//#endregion